import { useTranslation } from "react-i18next";
import { css, cx } from "@emotion/css";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { TextBlackButton } from "src/components/Default/LekUI/Button";
import { Menu } from "src/components/Default/LekUI/Menu";

export interface SortOption<TOrderBy extends string = string, TOrder extends string = string> {
	orderBy: TOrderBy;
	order: TOrder;
	labelKey: string;
	labelFallback: string;
}

interface SortButtonProps<TOrderBy extends string, TOrder extends string> {
	options: SortOption<TOrderBy, TOrder>[];
	selectedIndex: number;
	onSelect: (index: number) => void;
	className?: string;
	testId?: string;
	containerName?: string;
	collapseAt?: number;
}

const getCollapseStyles = (containerName: string, collapseAt: number) => ({
	btn: css`
		@container ${containerName} (max-width: ${collapseAt}px) {
			padding: 0;
			aspect-ratio: 1;
			min-width: unset;
		}
	`,
	label: css`
		@container ${containerName} (max-width: ${collapseAt}px) {
			display: none;
		}
	`,
	iconOnly: css`
		display: none;
		@container ${containerName} (max-width: ${collapseAt}px) {
			display: flex;
		}
	`,
});

export const SortButton = <TOrderBy extends string, TOrder extends string>({
	options,
	selectedIndex,
	onSelect,
	className,
	testId = "sort-button",
	containerName,
	collapseAt = 600,
}: SortButtonProps<TOrderBy, TOrder>) => {
	const { t } = useTranslation();
	const currentSort = options[selectedIndex];

	const collapse = containerName ? getCollapseStyles(containerName, collapseAt) : null;

	const orderLabel = (order: string) => {
		const lower = order.toLowerCase();
		return lower === "desc"
			? t("sort.descending", "Malejąco")
			: t("sort.ascending", "Rosnąco");
	};

	const menuItems = options.map((option, index) => ({
		title: `${t(option.labelKey, option.labelFallback)} (${orderLabel(option.order)})`,
		onClick: () => onSelect(index),
		selected: selectedIndex === index,
		testId: `${testId}-${option.orderBy}-${option.order}`,
	}));

	return (
		<Menu
			items={menuItems}
			closeAfterClick
			tooltip={t("sort.tooltip", "Sortuj")}
			renderTrigger={({ onClick }) => (
				<TextBlackButton
					className={cx(collapse?.btn, className)}
					onClick={onClick}
					data-test-id={testId}
				>
					<span className={cx(collapse?.label)}>
						{t(currentSort.labelKey, currentSort.labelFallback)}
					</span>
					<span className={cx(collapse?.label)}>
						{currentSort.order.toLowerCase() === "desc"
							? <ArrowDown className="icon-small" />
							: <ArrowUp className="icon-small" />}
					</span>
					<ArrowUpDown className={cx("icon-small", collapse?.iconOnly)} />
				</TextBlackButton>
			)}
		/>
	);
};
